import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';
import Auth from './Auth';
import supabase from '../supabase/supabase';

// DnD Kit Imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { 
  FiArrowLeft, FiArrowRight, FiCheckCircle, FiTarget, 
  FiLock, FiRocket, FiZap, FiShield, FiMail, FiUserPlus,
  FiSave, FiLoader, FiLogIn, FiChevronDown, FiChevronUp,
  FiMessageCircle, FiPlus, FiTrash2, FiBookOpen, FiEdit3,
  FiSmile, FiFrown, FiLayers, FiMoreVertical, FiUsers,
  FiUser, FiStar, FiFlag, FiActivity, FiVideo
} = FiIcons;

// Table constants
const STEP_2_TABLE = 'step_2_knowledge_mapping_1775471543474';
const STEP_4_TABLE = 'step_4_avatars_1775474655253';
const STEP_9_TABLE = 'step_9_mvp_features_1775481234567';
const WEBINAR_TABLE = 'webinar_registrations_1775485000000';

// --- Sortable Item Component for Step 2 ---
const SortableTopicItem = ({ topic, number, isActive, onClick, onDelete, onAddSubtopic }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: topic.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.5 : 1
  };

  const depth = (number.toString().match(/\./g) || []).length;

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`group flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all border-2 ${
        isActive 
          ? 'bg-blue-50 text-blue-700 border-blue-200 shadow-md ring-2 ring-blue-100 ring-offset-2' 
          : 'bg-white hover:bg-gray-50 text-gray-600 border-gray-100 hover:border-gray-200'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4 flex-grow min-w-0" style={{ marginLeft: `${depth * 24}px` }}>
        <div 
          {...attributes} 
          {...listeners} 
          className="cursor-grab active:cursor-grabbing p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          <SafeIcon icon={FiMoreVertical} className="text-lg" />
        </div>
        <div className="flex items-center space-x-3 overflow-hidden">
          <span className="text-sm font-black text-blue-500 bg-blue-50 px-2 py-1 rounded-lg flex-shrink-0 min-w-[2.5rem] text-center shadow-sm">
            {number}
          </span>
          <span className="text-base font-bold truncate">{topic.topic || '...'}</span>
        </div>
      </div>
      
      <div className="flex items-center space-x-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={(e) => { e.stopPropagation(); onAddSubtopic(topic.id); }}
          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-100 rounded-xl transition-all shadow-sm bg-white border border-gray-100"
          title="Add Subtopic"
        >
          <SafeIcon icon={FiPlus} className="text-sm" />
        </button>
        <button 
          onClick={(e) => { e.stopPropagation(); onDelete(topic.id); }}
          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-100 rounded-xl transition-all shadow-sm bg-white border border-gray-100"
          title="Delete Topic"
        >
          <SafeIcon icon={FiTrash2} className="text-sm" />
        </button>
      </div>
    </div>
  );
};

const StepDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = translations[lang].detail;
  const p = translations[lang].paths;
  const steps = stepsTranslations[lang];
  const stepId = parseInt(id);
  const step = steps.find(s => s.id === stepId);

  const [hasUnlockedAccess, setHasUnlockedAccess] = useState(false);
  const [user, setUser] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);

  // Refs
  const editorRef = useRef(null);
  const topicInputRef = useRef(null);

  // States
  const [step1Vision, setStep1Vision] = useState('');
  const [isSavingStep1, setIsSavingStep1] = useState(false);
  const [isSavedStep1, setIsSavedStep1] = useState(false);
  const [isStep1DropdownOpen, setIsStep1DropdownOpen] = useState(false);

  const [step2Topics, setStep2Topics] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [isSavingStep2, setIsSavingStep2] = useState(false);
  const [isSavedStep2, setIsSavedStep2] = useState(false);
  const [isCreatingTopic, setIsCreatingTopic] = useState(false);

  const [step3Data, setStep3Data] = useState({
    problem_solved: '',
    target_group: '',
    customer_needs: '',
    suitability: '',
    product_name_slogan: ''
  });
  const [isSavingStep3, setIsSavingStep3] = useState(false);
  const [isSavedStep3, setIsSavedStep3] = useState(false);

  const [step4Avatars, setStep4Avatars] = useState([]);
  const [activeAvatarId, setActiveAvatarId] = useState(null);
  const [isSavingStep4, setIsSavingStep4] = useState(false);
  const [isSavedStep4, setIsSavedStep4] = useState(false);

  const [step7Data, setStep7Data] = useState({
    product_description: '',
    target_group: '',
    goal: '',
    shortcut: '',
    journey_before: '',
    journey_after: ''
  });
  const [isSavingStep7, setIsSavingStep7] = useState(false);
  const [isSavedStep7, setIsSavedStep7] = useState(false);

  const [step9Features, setStep9Features] = useState([]);
  const [isSavingStep9, setIsSavingStep9] = useState(false);
  const [isSavedStep9, setIsSavedStep9] = useState(false);
  const [isRegisteringWebinar, setIsRegisteringWebinar] = useState(false);
  const [isWebinarRegistered, setIsWebinarRegistered] = useState(false);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const checkUser = async () => {
      setLoadingAccess(true);
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
      setLoadingAccess(false);
    };
    checkUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setHasUnlockedAccess(!!currentUser);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      if (stepId === 1) {
        const { data } = await supabase.from('step_1_vision_1773100000000').select('vision_text').eq('user_id', user.id).single();
        if (data) setStep1Vision(data.vision_text);
      } else if (stepId === 2) {
        const { data } = await supabase.from(STEP_2_TABLE).select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
        if (data) {
          setStep2Topics(data);
          if (data.length > 0) setActiveTopicId(data[0].id);
        }
      } else if (stepId === 3) {
        const { data } = await supabase.from('step_3_ideas_1772902600000').select('*').eq('user_id', user.id).single();
        if (data) setStep3Data(data);
      } else if (stepId === 4) {
        const { data } = await supabase.from(STEP_4_TABLE).select('*').eq('user_id', user.id).order('created_at', { ascending: true });
        if (data && data.length > 0) {
          setStep4Avatars(data);
          setActiveAvatarId(data[0].id);
        }
      } else if (stepId === 7) {
        const { data } = await supabase.from('step_7_offers_1773460000000').select('*').eq('user_id', user.id).single();
        if (data) setStep7Data(data);
      } else if (stepId === 9) {
        const { data } = await supabase.from(STEP_9_TABLE).select('*').eq('user_id', user.id).order('sort_order', { ascending: true });
        if (data) setStep9Features(data);

        const { data: webinarData } = await supabase.from(WEBINAR_TABLE).select('id').eq('user_id', user.id).single();
        if (webinarData) setIsWebinarRegistered(true);
      }
    };
    fetchData();
  }, [stepId, user]);

  // Step 9 Handlers
  const handleAddStep9Feature = async () => {
    if (!user) return;
    const newFeature = { user_id: user.id, feature_name: '', priority: 'Medium', status: 'Planned', sort_order: step9Features.length };
    const { data, error } = await supabase.from(STEP_9_TABLE).insert(newFeature).select().single();
    if (data) setStep9Features([...step9Features, data]);
  };

  const handleUpdateStep9Feature = (id, field, value) => {
    setStep9Features(prev => prev.map(f => f.id === id ? { ...f, [field]: value } : f));
  };

  const handleDeleteStep9Feature = async (id) => {
    const { error } = await supabase.from(STEP_9_TABLE).delete().eq('id', id);
    if (!error) setStep9Features(prev => prev.filter(f => f.id !== id));
  };

  const handleStep9Save = async () => {
    if (!user) return;
    setIsSavingStep9(true);
    const { error } = await supabase.from(STEP_9_TABLE).upsert(step9Features.map(f => ({ ...f, updated_at: new Date().toISOString() })));
    setIsSavingStep9(false);
    if (!error) { setIsSavedStep9(true); setTimeout(() => setIsSavedStep9(false), 3000); }
  };

  const handleWebinarRegistration = async () => {
    if (!user || isWebinarRegistered) return;
    setIsRegisteringWebinar(true);
    try {
      const { error } = await supabase.from(WEBINAR_TABLE).insert({ user_id: user.id });
      if (error) throw error;
      setIsWebinarRegistered(true);
    } catch (err) {
      console.error('Error registering for webinar:', err);
    } finally {
      setIsRegisteringWebinar(false);
    }
  };

  // Other Step Handlers
  const handleStep1Save = async () => {
    if (!user) return;
    setIsSavingStep1(true);
    const { error } = await supabase.from('step_1_vision_1773100000000').upsert({ user_id: user.id, vision_text: step1Vision, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep1(false);
    if (!error) { setIsSavedStep1(true); setTimeout(() => setIsSavedStep1(false), 3000); }
  };

  const handleStep3Save = async () => {
    if (!user) return;
    setIsSavingStep3(true);
    const { error } = await supabase.from('step_3_ideas_1772902600000').upsert({ user_id: user.id, ...step3Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep3(false);
    if (!error) { setIsSavedStep3(true); setTimeout(() => setIsSavedStep3(false), 3000); }
  };

  const handleStep7Save = async () => {
    if (!user) return;
    setIsSavingStep7(true);
    const { error } = await supabase.from('step_7_offers_1773460000000').upsert({ user_id: user.id, ...step7Data, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
    setIsSavingStep7(false);
    if (!error) { setIsSavedStep7(true); setTimeout(() => setIsSavedStep7(false), 3000); }
  };

  // Step 2 Drag Helpers
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setStep2Topics((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newArray = arrayMove(items, oldIndex, newIndex);
      return newArray.map((item, index) => ({ ...item, sort_order: index }));
    });
  };

  const renderLockedOverlay = () => (
    <div className="absolute inset-x-0 top-0 bottom-0 z-20 flex items-center justify-center p-6 bg-blue-900/10 backdrop-blur-[12px] rounded-[2.5rem]">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white/95 p-8 md:p-10 rounded-[2.5rem] shadow-2xl border border-blue-100 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner"><SafeIcon icon={FiLock} className="text-3xl text-blue-600" /></div>
          <h4 className="text-2xl font-black text-gray-900 mb-3 uppercase tracking-tight">{t.lockedTitle}</h4>
          <p className="text-gray-600 font-medium">{t.authPrompt}</p>
        </div>
        <div className="bg-gray-50/50 p-6 rounded-[2rem] border border-gray-100 shadow-inner"><Auth embedded={true} initialIsLogin={false} /></div>
      </motion.div>
    </div>
  );

  if (!step) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-24">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <Link to={p.steps} className="group flex items-center space-x-2 text-gray-500 hover:text-blue-600 transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 italic">
            <SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" /><span className="text-sm font-bold">{t.back}</span>
          </Link>
          <span className="text-sm font-bold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">{t.stepOf.replace('{current}', stepId)}</span>
        </div>

        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 mb-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none font-black text-[200px] leading-none text-blue-900">{stepId}</div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10">
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6"><SafeIcon icon={FiTarget} /><span>{t.overview}</span></div>
            <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4 tracking-tight leading-tight">{step.title}</h1>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-sm border border-gray-100 relative">
              <h2 className="text-2xl font-black text-gray-900 mb-6 flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiTarget} className="text-blue-600" /></div>
                <span>{t.overview}</span>
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed font-medium">{step.description}</p>
            </div>

            <div className="relative">
              <div className={`space-y-8 transition-all duration-700 ${!hasUnlockedAccess ? 'blur-xl grayscale opacity-30 select-none' : ''}`}>
                
                {/* Step 1 Tool */}
                {stepId === 1 && (
                  <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                    <div className="flex items-center space-x-3 mb-8"><div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><SafeIcon icon={FiRocket} className="text-blue-600 text-xl" /></div><h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step1ToolTitle}</h3></div>
                    <textarea value={step1Vision} onChange={(e) => setStep1Vision(e.target.value)} placeholder={t.step1Placeholder} className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl p-6 focus:border-blue-500 focus:ring-0 transition-all min-h-[250px] font-medium text-gray-700 shadow-inner text-lg leading-relaxed" />
                    <div className="pt-4 flex items-center justify-end">
                      <button onClick={handleStep1Save} className={`flex items-center space-x-2 px-10 py-4 rounded-full font-black transition-all ${isSavedStep1 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                        {isSavingStep1 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep1 ? FiCheckCircle : FiSave} />}
                        <span>{isSavingStep1 ? t.step1Saving : (isSavedStep1 ? t.step1Saved : t.step1Save)}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 9 Tool - Co-Create MVP & Webinar CTA */}
                {stepId === 9 && (
                  <div className="space-y-8">
                    {/* Congratulatory Section */}
                    <div className="bg-white p-8 md:p-10 shadow-sm border border-gray-100 rounded-[2.5rem]">
                      <div className="flex items-center space-x-3 mb-8">
                        <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                          <SafeIcon icon={FiRocket} className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-2xl font-black uppercase tracking-tight text-gray-900">{t.step9ToolTitle}</h3>
                      </div>
                      
                      <div className="mb-10 p-8 bg-blue-50 rounded-3xl border border-blue-100 italic text-gray-700 text-lg leading-relaxed shadow-inner">
                        {t.step9ExplanatoryText}
                      </div>

                      <div className="overflow-x-auto mb-8">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-100">
                              <th className="text-left py-4 px-2 text-xs font-black text-gray-400 uppercase tracking-widest">{t.step9FeatureName}</th>
                              <th className="text-left py-4 px-2 text-xs font-black text-gray-400 uppercase tracking-widest w-40">{t.step9Priority}</th>
                              <th className="text-left py-4 px-2 text-xs font-black text-gray-400 uppercase tracking-widest w-40">{t.step9Status}</th>
                              <th className="py-4 px-2 w-16"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-50">
                            {step9Features.map((feature) => (
                              <tr key={feature.id} className="group">
                                <td className="py-4 px-2">
                                  <input 
                                    type="text" 
                                    value={feature.feature_name} 
                                    onChange={(e) => handleUpdateStep9Feature(feature.id, 'feature_name', e.target.value)}
                                    className="w-full bg-transparent border-none focus:ring-0 font-bold text-gray-900 p-0 placeholder-gray-300"
                                    placeholder="e.g., User Authentication..."
                                  />
                                </td>
                                <td className="py-4 px-2">
                                  <select 
                                    value={feature.priority}
                                    onChange={(e) => handleUpdateStep9Feature(feature.id, 'priority', e.target.value)}
                                    className="bg-gray-50 border-none rounded-xl text-sm font-black text-gray-600 focus:ring-0 cursor-pointer"
                                  >
                                    <option value="High">{lang === 'de' ? 'Hoch' : 'High'}</option>
                                    <option value="Medium">{lang === 'de' ? 'Mittel' : 'Medium'}</option>
                                    <option value="Low">{lang === 'de' ? 'Niedrig' : 'Low'}</option>
                                  </select>
                                </td>
                                <td className="py-4 px-2">
                                  <select 
                                    value={feature.status}
                                    onChange={(e) => handleUpdateStep9Feature(feature.id, 'status', e.target.value)}
                                    className="bg-gray-50 border-none rounded-xl text-sm font-black text-gray-600 focus:ring-0 cursor-pointer"
                                  >
                                    <option value="Planned">{lang === 'de' ? 'Geplant' : 'Planned'}</option>
                                    <option value="In Progress">{lang === 'de' ? 'In Arbeit' : 'In Progress'}</option>
                                    <option value="Done">{lang === 'de' ? 'Erledigt' : 'Done'}</option>
                                  </select>
                                </td>
                                <td className="py-4 px-2 text-right">
                                  <button onClick={() => handleDeleteStep9Feature(feature.id)} className="p-2 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100">
                                    <SafeIcon icon={FiTrash2} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <button onClick={handleAddStep9Feature} className="flex items-center space-x-2 px-6 py-4 rounded-2xl font-black bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all text-sm border-2 border-blue-100 active:scale-95">
                          <SafeIcon icon={FiPlus} /><span>{t.step9AddFeature}</span>
                        </button>
                        <button onClick={handleStep9Save} disabled={isSavingStep9} className={`flex items-center space-x-2 px-10 py-5 rounded-full font-black transition-all shadow-lg active:scale-95 ${isSavedStep9 ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                          {isSavingStep9 ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={isSavedStep9 ? FiCheckCircle : FiSave} />}
                          <span>{isSavingStep9 ? t.step9Saving : (isSavedStep9 ? t.step9Saved : t.step9Save)}</span>
                        </button>
                      </div>
                    </div>

                    {/* Webinar CTA Section - CRITICAL: Register Button */}
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[2.5rem] p-8 md:p-12 text-white shadow-xl shadow-blue-200 relative overflow-hidden group">
                      <div className="absolute top-0 right-0 p-12 opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-700">
                        <SafeIcon icon={FiVideo} className="text-[200px]" />
                      </div>
                      
                      <div className="relative z-10 max-w-2xl">
                        <div className="inline-flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-6">
                          <SafeIcon icon={FiStar} className="text-yellow-300" />
                          <span>{lang === 'de' ? 'Kostenloses Training' : 'Free Training'}</span>
                        </div>
                        
                        <h3 className="text-3xl md:text-4xl font-black mb-6 leading-tight">
                          {t.step9WebinarTitle}
                        </h3>
                        
                        <p className="text-lg text-blue-50 font-medium mb-10 leading-relaxed">
                          {t.step9WebinarDesc}
                        </p>
                        
                        {isWebinarRegistered ? (
                          <div className="inline-flex items-center space-x-3 bg-green-500 text-white px-8 py-4 rounded-2xl font-black shadow-lg">
                            <SafeIcon icon={FiCheckCircle} className="text-2xl" />
                            <span>{t.step9WebinarSuccess}</span>
                          </div>
                        ) : (
                          <button 
                            onClick={handleWebinarRegistration}
                            disabled={isRegisteringWebinar}
                            className="flex items-center space-x-3 px-10 py-5 bg-white text-blue-600 rounded-2xl font-black hover:bg-blue-50 transition-all shadow-xl active:scale-95 disabled:opacity-50"
                          >
                            {isRegisteringWebinar ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiMail} />}
                            <span>{t.step9WebinarCTA}</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {!hasUnlockedAccess && renderLockedOverlay()}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white shadow-xl sticky top-24 border border-gray-800">
              <div className="flex items-center space-x-3 mb-6"><SafeIcon icon={FiTarget} className="text-blue-400 text-xl" /><h3 className="font-black uppercase tracking-widest text-sm">{t.keyFocus}</h3></div>
              <ul className="space-y-4 mb-8">{step.keyPoints?.map((point, i) => (<li key={i} className="flex items-center space-x-3 p-4 rounded-2xl bg-white/5 border border-white/5 transition-all text-gray-300"><SafeIcon icon={FiArrowRight} className="text-blue-400 text-xs flex-shrink-0" /><span className="font-bold text-sm">{point}</span></li>))}</ul>
              <div className="grid grid-cols-2 gap-3 pt-6 border-t border-white/10">
                {stepId > 1 ? <button onClick={() => navigate(`/steps/${stepId - 1}`)} className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-gray-300 font-bold text-sm group"><SafeIcon icon={FiArrowLeft} className="transition-transform group-hover:-translate-x-1" /><span>{t.prevStep}</span></button> : <div />}
                {stepId < 10 ? <button onClick={() => navigate(`/steps/${stepId + 1}`)} className="flex items-center justify-center space-x-2 p-4 rounded-2xl bg-blue-600 hover:bg-blue-700 transition-all text-white font-bold text-sm group"><span>{t.nextStep}</span><SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-1" /></button> : <div />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepDetail;